import { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'formik';
import * as Yup from 'yup';
import _map from 'lodash/map';
import _trim from 'lodash/trim';
import _get from 'lodash/get';
import Router from 'next/router';
import { useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MaterialTextField from '@material-ui/core/TextField';
import MLink from '@material-ui/core/Link';
import DialogContentText from '@material-ui/core/DialogContentText';
import TextField from '../forms/text-field';
import TextAreaField from '../forms/text-area-field';
import SelectField from '../forms/select-field';
import { eventTypes } from '../../utils/events';
import { combineDateAndTime } from '../../utils/time';
import { removeEventMutation, cancelEventMutation } from '../../apollo/events';
import ConfirmButton from '../confirm-button';
import FormSubmitStatus from '../forms/form-submit-status';
import {
  getEventCategoryIds,
} from '../../utils/event-categories';
import { useAppContext } from '../../contexts/app';
import FormLocation from './form-location';
import FormImage from './form-image';
import FormCategories from './form-categories';
import FormPaces from './form-paces';
import FormDistances from './form-distances';
import FormTerrains from './form-terrains';
import FormRegroups from './form-regroups';
import FormWeatherCancellations from './form-weather-cancellations';
import FormAgeRestriction from './form-age-restriction';
import FormRegistration from './form-registration';
import FormGroup from './form-group';
import FormHost from './form-host';
import FormTime from './form-time';
import FormRouteUrl from './form-route-url';
import DuplicateDropdown from './duplicate-dropdown';
import FormStatus from './form-status';
import FormNotificationsSummary from './form-notifications-summary';

const useStyles = makeStyles((theme) => ({
  submitButtons: {
    marginTop: theme.spacing(2),
    '& > *:first-child': {
      marginRight: theme.spacing(1)
    },
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1)
    }
  },
  deleteButton: {
    color: red.A400
  },
  cityField: {
    marginTop: theme.spacing(2)
  },
  copyEventButton: {
    color: '#555'
  },
  copyContactButton: {
    margin: theme.spacing(0, 0, 2, 0),
  },
  metaSelect: {
    margin: theme.spacing(2, 0),
  },
  supportBanner: {
    margin: theme.spacing(4, 0, 0, 0),
  },
}));

export const eventSchema = Yup.object().shape({
  hostType: Yup.string()
    .oneOf(['User', 'Group'])
    .required(),
  groupId: Yup.string().nullable(),
  eventType: Yup.string()
    .oneOf(['RIDE', 'EVENT', 'ONLINE_EVENT'])
    .required('Event type is required'),
  name: Yup.string().nullable().required('Name is required'),
  visibility: Yup
    .string()
    .oneOf(['PUBLIC', 'UNLISTED', 'PRIVATE', 'GROUP'])
    .required('Visibility is required'),
  description: Yup.string().nullable().required('Description is required'),
  startDate: Yup
    .date()
    .typeError('Start date must be a date with format MM/DD/YYYY')
    .nullable()
    .when(
      'published',
      {
        is: true,
        then: (s) => s.required('Start date is required')
      }
    ),
  startTime: Yup
    .date()
    .typeError('Start time must be a time with format HH:MM AM/PM')
    .nullable()
    .when(
      'published',
      {
        is: true,
        then: (s) => s.required('Start time is required')
      }
    ),
  startAtDetails: Yup.string().nullable(),
  endDate: Yup
    .date()
    .typeError('End date must be a date with format MM/DD/YYYY')
    .nullable(),
  endTime: Yup
    .date()
    .typeError('End time must be a time with format HH:MM AM/PM')
    .nullable(),
  startLocation: Yup.string().nullable(),
  startLocationAddress: Yup.string().nullable(),
  startLocationDetails: Yup.string().nullable(),
  loop: Yup.boolean(),
  pace: Yup.string().nullable(),
  distance: Yup.string().nullable(),
  regroup: Yup.string().nullable(),
  terrain: Yup.string().nullable(),
  weatherCancellation: Yup.string().nullable(),
  audienceRestriction: Yup.string().nullable(),
  ageRestriction: Yup
    .string()
    .nullable()
    .oneOf(['GENERAL', 'FAMILY', '21_PLUS'])
    .required('Age Restriction is required'),
  endLocation: Yup.string().nullable(),
  endLocationAddress: Yup.string().nullable(),
  endLocationDetails: Yup.string().nullable(),
  infoUrl: Yup.string().nullable(),
  routeUrl: Yup.string().nullable(),
  image: Yup.mixed().nullable(),
  categoryIds: Yup.array()
    .of(Yup.string().oneOf(getEventCategoryIds()))
    .ensure(),
  approvalPrompt: Yup.string().nullable(),
  approvalDescription: Yup.string().nullable(),
  contactName: Yup.string().nullable(),
  contactPhoneNumber: Yup.string().nullable(),
  contactEmail: Yup.string().nullable(),
  currentImage: Yup.object().shape({
    filename: Yup.string()
  }).nullable(),
  status: Yup
    .string()
    .oneOf(['DRAFT', 'PUBLISHED', 'GROUP_PENDING'])
    .required('Status is required'),
  registrationType: Yup
    .string()
    .oneOf(['OPEN', 'EXTERNAL_REGISTRATION', 'APPROVAL_REQUIRED'])
    .required('Registration Type is required'),
}).test('end-after-start', function testEndAfterStart(values) {
  const startAt = combineDateAndTime(values.startDate, values.startTime);
  const endAt = combineDateAndTime(values.endDate, values.endTime);
  if (startAt && endAt && startAt.isValid() && endAt.isValid() && endAt.isBefore(startAt)) {
    return this.createError({
      path: 'endDate',
      message: 'End time must be after start time',
    });
  }
  return true;
}).test('host-group-selected', function testHostGroupSelected(values) {
  const { hostType, groupId } = values;
  if (hostType === 'Group' && !groupId) {
    return this.createError({
      path: 'groupId',
      message: 'Group must be selected if host is a group.',
    });
  }
}).test('privacy-group-selected', function testPrivacyGroupSelected(values) {
  const { visibility, groupId } = values;
  if (visibility === 'GROUP' && !groupId) {
    return this.createError({
      path: 'groupId',
      message: 'Group must be selected if privacy is group.',
    });
  }
}).test('published-has-location', function testPublishedHasLocation(values) {
  const { status, startLocation, eventType } = values;
  if (eventType !== 'ONLINE_EVENT' && status === 'PUBLISHED' && (!startLocation || !_trim(startLocation))) {
    return this.createError({
      path: 'startLocation',
      message: 'Start location is required when publishing.',
    });
  }
});

const EventForm = (props) => {
  const {
    event,
    isSubmitting,
    submitCount,
    setFieldValue,
    disabled: formDisabled,
    isEventSeries,
  } = props;

  const [cancelEvent] = useMutation(cancelEventMutation);
  const [removeEvent] = useMutation(removeEventMutation);

  const { loggedInUser } = useAppContext();

  const classes = useStyles();
  const [deleting, setDeleting] = useState(false);
  const [cancellationMessage, setCancellationMessage] = useState('');
  const disabled = formDisabled || isSubmitting || deleting;
  const forceTouched = submitCount > 0;

  return (
    <Form>
      <SelectField
        name="visibility"
        label="Privacy"
        disabled={disabled || isEventSeries}
        touched={forceTouched}
        options={[
          {
            label: 'Public (included on city calendar)',
            value: 'PUBLIC',
          },
          {
            label: `🔒 Group (only in a group)`,
            value: 'GROUP',
          },
          {
            label: `🔒 Unlisted (accessible with link)`,
            value: 'UNLISTED',
          },
          {
            label: `🔒 Private (invite-only)`,
            value: 'PRIVATE',
          },
        ]}
      />
      <FormGroup
        disabled={disabled || isEventSeries}
        forceTouched={forceTouched}
        group={_get(event, 'group')}
      />
      <FormHost
        disabled={disabled || isEventSeries}
        forceTouched={forceTouched}
        user={_get(event, 'user')}
        group={_get(event, 'group')}
      />
      <SelectField
        name="eventType"
        label="Event Type"
        disabled={disabled}
        touched={forceTouched}
        options={_map(eventTypes, ({ name }, value) => ({ label: name, value }))}
        className={classes.eventTypeField}
      />
      <TextField
        name="name"
        label="Name"
        disabled={disabled}
        touched={forceTouched}
      />

      <FormTime
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <FormLocation
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <Typography variant="h2">Details</Typography>
      <TextAreaField
        name="description"
        label="Description"
        disabled={disabled}
        touched={forceTouched}
        helperText={(
          <span>
            <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
              Markdown
            </MLink>
            {' '}is supported
          </span>
        )}
        minRows={6}
      />
      <TextField
        name="infoUrl"
        label="Info URL (optional)"
        helperText="Registration page or primary event URL"
        disabled={disabled}
        touched={forceTouched}
      />
      <FormRouteUrl
        disabled={disabled}
        touched={forceTouched}
      />
      <FormAgeRestriction
        disabled={disabled}
        touched={forceTouched}
        className={classes.metaSelect}
      />
      <TextField
        name="audienceRestriction"
        label="Audience Restriction (optional)"
        disabled={disabled}
        touched={forceTouched}
        helperText={`Example: "WTF/NB" or "Asian/NH/PI"`}
      />
      <FormPaces
        disabled={disabled}
        touched={forceTouched}
        className={classes.metaSelect}
      />
      <FormDistances
        disabled={disabled}
        touched={forceTouched}
        className={classes.metaSelect}
      />
      <FormTerrains
        disabled={disabled}
        touched={forceTouched}
        className={classes.metaSelect}
      />
      <FormRegroups
        disabled={disabled}
        touched={forceTouched}
        className={classes.metaSelect}
      />
      <FormWeatherCancellations
        disabled={disabled}
        touched={forceTouched}
        className={classes.metaSelect}
      />
      <FormCategories
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <FormImage
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <Typography variant="h2">Contact Information</Typography>
      <p>
        Contact information is shared with everyone who has access to the event.
      </p>
      <Button
        onClick={() => {
          setFieldValue('contactName', loggedInUser.displayName);
          setFieldValue('contactEmail', loggedInUser.email);
          setFieldValue('contactPhoneNumber', loggedInUser.phoneNumberDisplay);
        }}
        size="small"
        variant="contained"
        className={classes.copyContactButton}
        disabled={disabled}
      >
        Copy my contact information
      </Button>
      <TextField
        name="contactName"
        label="Contact Name (optional)"
        disabled={disabled}
        touched={forceTouched}
      />
      <TextField
        name="contactEmail"
        label="Contact Email (optional)"
        disabled={disabled}
        touched={forceTouched}
      />
      <TextField
        name="contactPhoneNumber"
        label="Contact Phone Number (optional)"
        disabled={disabled}
        touched={forceTouched}
      />

      <FormRegistration
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <FormStatus
        event={event}
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <FormNotificationsSummary
        event={event}
        isEventSeries={isEventSeries}
      />

      <div className={classes.submitButtons}>
        <Button
          type="submit"
          disabled={disabled}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
        {event && (
          <Fragment>
            <ConfirmButton
              label="Cancel Event"
              message="Are you sure you want to cancel this event?"
              onClick={() => {
                setDeleting(true);
                cancelEvent({
                  variables: {
                    id: event.id,
                    message: cancellationMessage
                  }
                }).then(() => Router.push('/events/[id]', `/events/${event.id}`));
              }}
              disabled={disabled}
            >
              <DialogContentText>
                Are you sure you want to delete this event?
              </DialogContentText>
              <MaterialTextField
                autoFocus
                id="cancellationMessage"
                label="Cancellation Message (optional)"
                fullWidth
                multiline
                rows={3}
                maxRows={20}
                variant="outlined"
                value={cancellationMessage}
                onChange={(e) => {
                  setCancellationMessage(e.target.value);
                }}
              />
            </ConfirmButton>
            <ConfirmButton
              label="Delete Event"
              onClick={() => {
                setDeleting(true);
                removeEvent({
                  variables: { id: event.id }
                }).then(() => Router.push('/calendar'));
              }}
              disabled={deleting}
              className={classes.deleteButton}
            >
              <DialogContentText>
                Are you sure you want to delete this event?
              </DialogContentText>
              <DialogContentText>
                Attendees will not be notified. If you&lsquo;d like to notify attendees, choose Cancel Event instead.
              </DialogContentText>
            </ConfirmButton>
            <DuplicateDropdown
              event={event}
              className={classes.copyEventButton}
            />
          </Fragment>
        )}
        <FormSubmitStatus {...props} />
      </div>
    </Form>
  );
};

EventForm.propTypes = {
  isSubmitting: PropTypes.bool,
  values: PropTypes.object,
  submitCount: PropTypes.number,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  event: PropTypes.object,
  setFieldValue: PropTypes.func,
  isEventSeries: PropTypes.bool,
};

EventForm.defaultProps = {
  isEventSeries: false,
};

export default EventForm;
